import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import { marketingOrigin } from "../data/seo/siteEntity";
import { COMPARISON_CATEGORIES, getComparisonsByCategory, COMPARISONS } from "../data/comparisons";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { buildCollectionPageSchema } from "../data/seo/structuredData";
import type { FaqItem } from "../components/seo/FAQSection";

const SEO_TITLE = "Best AI Voice Agent Alternatives — FloGPT";
const SEO_DESCRIPTION =
    "Looking for an alternative to Vapi, Bland, Retell, or Synthflow? FloGPT runs AI voice and chat across seven channels with a built-in CRM inbox and knowledge base. Compare your options.";
const SEO_KEYWORDS = [
    "AI voice agent alternatives",
    "Vapi alternative",
    "Bland alternative",
    "Retell alternative",
    "Synthflow alternative",
    "best AI calling platform",
    "AI customer support platform",
];

const HUB_FAQS: FaqItem[] = [
    {
        question: "What is the best alternative to voice-only AI platforms?",
        answer: "FloGPT is a strong alternative when you need more than phone calls. It runs AI voice and a website chat widget, unifies email, SMS, WhatsApp, Instagram, and Facebook into one CRM inbox, and includes a managed knowledge base — so you get a complete customer platform instead of a single voice channel.",
    },
    {
        question: "Why choose FloGPT over a dedicated voice-AI tool?",
        answer: "Dedicated voice tools excel at phone calls but leave you to build chat, messaging, a knowledge base, contact identity, and ticketing yourself. FloGPT ships all of that together, with no-code builders and 13 vertical templates, so a non-engineer can launch and operate AI support end to end.",
    },
    {
        question: "Can FloGPT replace my AI calling provider?",
        answer: "Yes. FloGPT handles inbound and outbound calls with sub-500ms latency, warm transfer to humans, and an outbound trigger engine (webhooks, Shopify events, scheduled lists, time-relative cadences). You bring your own numbers and rebuild call flows visually in the Call Pipeline editor.",
    },
    {
        question: "Does FloGPT support Indian languages and DLT compliance?",
        answer: "Yes. FloGPT supports 16 languages including Hindi and nine Indic locales, detects the caller's language automatically, and routes India outbound traffic through DLT-compliant Jio and MSG91 providers.",
    },
];

export default function AlternativesHub() {
    usePageSeo({
        title: SEO_TITLE,
        description: SEO_DESCRIPTION,
        keywords: SEO_KEYWORDS.join(", "),
        canonical: "/alternatives",
    });

    const collectionSchema = useMemo(
        () =>
            buildCollectionPageSchema({
                name: "AI voice agent alternatives — FloGPT",
                description: SEO_DESCRIPTION,
                path: "/alternatives",
                itemCount: COMPARISONS.length,
            }),
        [],
    );

    const breadcrumbSchema = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${marketingOrigin()}/` },
                { "@type": "ListItem", position: 2, name: "Alternatives", item: `${marketingOrigin()}/alternatives` },
            ],
        }),
        [],
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden text-slate-100">
            <LandingNavbar />

            <main className="pt-20">
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 -z-10">
                        <div className="absolute -left-32 top-10 h-72 w-72 rounded-full bg-orange-500/15 blur-3xl" />
                        <div className="absolute -right-32 bottom-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
                    </div>

                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="max-w-3xl"
                        >
                            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                                Alternatives
                            </div>
                            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
                                A complete{" "}
                                <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
                                    AI voice & chat
                                </span>{" "}
                                alternative
                            </h1>
                            <p className="mt-6 text-lg leading-relaxed text-slate-300">
                                Evaluating voice-AI platforms? FloGPT is the alternative for teams that want voice and
                                chat, seven messaging channels, a built-in CRM inbox, and a managed knowledge base in a
                                single no-code product — not a developer toolkit or a phone-only tool.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {COMPARISON_CATEGORIES.map((category) => {
                    const items = getComparisonsByCategory(category.key);
                    if (items.length === 0) return null;
                    return (
                        <section key={category.key} className="relative pb-16">
                            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                                <div className="max-w-3xl mb-8">
                                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                                        {category.label}
                                    </h2>
                                    <p className="mt-3 text-slate-400 leading-relaxed">{category.blurb}</p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                                    {items.map((c) => {
                                        const Icon = c.Icon;
                                        return (
                                            <Link
                                                key={c.slug}
                                                to={`/compare/${c.slug}`}
                                                className="group rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 hover:border-white/20 hover:bg-white/10 transition-all"
                                            >
                                                <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center shadow-lg mb-3`}>
                                                    <Icon className="h-5 w-5 text-white" />
                                                </div>
                                                <h3 className="text-base font-semibold text-white group-hover:text-orange-400 transition-colors">
                                                    {c.competitor} alternative
                                                </h3>
                                                <p className="mt-1 text-sm text-slate-400 leading-relaxed line-clamp-2">{c.tagline}</p>
                                                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-orange-400">
                                                    FloGPT vs {c.competitor}
                                                    <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        </section>
                    );
                })}

                <FAQSection
                    id="alternatives-hub"
                    eyebrow="Alternatives · FAQ"
                    heading="Choosing an AI voice & chat alternative"
                    intro="Common questions when evaluating FloGPT against voice-only AI platforms."
                    items={HUB_FAQS}
                />
            </main>

            <Footer />

            <JsonLd id="alternatives-collection" data={collectionSchema} />
            <JsonLd id="alternatives-breadcrumb" data={breadcrumbSchema} />
        </div>
    );
}
